/**
 * Système de queue d'actions pour le jeu de Tarot
 * 
 * Inspiré du système de jobs du client legacy v2, mais modernisé avec TypeScript et React.
 * 
 * Principe:
 * - TOUS les messages du serveur sont convertis en actions et ajoutés à une queue
 * - Les actions sont traitées séquentiellement, une par une
 * - Certaines actions ont un délai (pour les animations)
 * - L'UI ne change que quand l'action est "exécutée", pas quand elle est reçue
 * 
 * Cela garantit que:
 * - Les cartes s'affichent avec un délai entre elles
 * - Le pli n'est pas effacé avant que toutes les cartes soient affichées
 * - Un nouveau pli ne commence pas avant que l'ancien soit terminé
 */

import { CardNumber, Contract } from '../types/game';
import { log } from '../utils/logger';

// ============================================
// Types pour l'état du jeu
// ============================================

export interface Player {
  name: string;
  position: number;
  isConnected: boolean;
  tricksWon?: number;
  lastBid?: Contract;
  /** Partenaire révélé (5 joueurs) */
  isPartner?: boolean;
}

export interface TimeLimits {
  contrat: number;  // Temps max pour enchérir (secondes)
  appel: number;    // Temps max pour appeler le roi (secondes)
  ecart: number;    // Temps max pour faire l'écart (secondes)
  carte: number;    // Temps max pour jouer une carte (secondes)
  score: number;    // Temps max pour se dire "prêt" après le score (secondes)
}

/** Types de poignée */
export enum PoigneeType {
  None = 0,
  Simple = 1,
  Double = 2,
  Triple = 3,
}

/**
 * Seuils de poignée selon le nombre de joueurs.
 * 
 * Règle officielle :
 * - 3 joueurs : Simple = 13, Double = 15, Triple = 18
 * - 4 joueurs : Simple = 10, Double = 13, Triple = 15
 * - 5 joueurs : Simple =  8, Double = 10, Triple = 13
 */
const POIGNEE_THRESHOLDS_BY_PLAYERS: Record<number, Record<PoigneeType, number>> = {
  3: {
    [PoigneeType.None]: 0,
    [PoigneeType.Simple]: 13,
    [PoigneeType.Double]: 15,
    [PoigneeType.Triple]: 18,
  },
  4: {
    [PoigneeType.None]: 0,
    [PoigneeType.Simple]: 10,
    [PoigneeType.Double]: 13,
    [PoigneeType.Triple]: 15,
  },
  5: {
    [PoigneeType.None]: 0,
    [PoigneeType.Simple]: 8,
    [PoigneeType.Double]: 10,
    [PoigneeType.Triple]: 13,
  },
};

/** Seuils de poignée par défaut (4 joueurs) — rétro-compatibilité */
export const POIGNEE_THRESHOLDS: Record<PoigneeType, number> = POIGNEE_THRESHOLDS_BY_PLAYERS[4];

/** Retourne les seuils de poignée pour un nombre de joueurs donné */
export function getPoigneeThresholds(playerCount: number): Record<PoigneeType, number> {
  return POIGNEE_THRESHOLDS_BY_PLAYERS[playerCount] || POIGNEE_THRESHOLDS_BY_PLAYERS[4];
}

/** Noms français des poignées */
export const PoigneeNames: Record<PoigneeType, string> = {
  [PoigneeType.None]: '',
  [PoigneeType.Simple]: 'Poignée simple',
  [PoigneeType.Double]: 'Poignée double',
  [PoigneeType.Triple]: 'Poignée triple',
};

/** Données d'une poignée déclarée (pour affichage) */
export interface DeclaredPoignee {
  player: number;
  type: PoigneeType;
  cards: CardNumber[];
}

export interface GameState {
  // Identification
  tableId: string;
  idPartie?: string;
  
  // Phase du jeu
  // 0=Waiting, 1=Seated, 2=Bidding, 3=KingCall, 4=ViewChien, 5=MakeEcart, 6=Playing, 99999=Finished
  phase: number;
  
  // Joueurs
  playerCount: number;
  players: Player[];
  myPosition: number;
  hasOtherHumans: boolean;  // true si d'autres humains à la table (pour afficher le chat)
  /** Neo : admin de la table (démarrage manuel si besoin) */
  neoAdminPseudo?: string;
  /** Neo : lobby en attente de connexion / démarrage */
  neoWaitingLobby?: boolean;
  
  // Jeu
  dealer: number;
  currentPlayer: number;
  entameur?: number;
  taker?: number;
  currentContract?: Contract;
  /** Carte appelée (roi ou dame) — 5 joueurs */
  calledKing?: CardNumber;
  /** Position du partenaire une fois révélé */
  partner?: number;
  /** Le partenaire a été identifié (carte appelée jouée) */
  partnerRevealed?: boolean;
  /** Révélation du roi appelé en cours (défenseurs, 5 joueurs) */
  kingCallRevealPending?: boolean;
  /** Chien retenu tant que la révélation du roi n'est pas fermée */
  pendingChienReveal?: CardNumber[];
  pendingChienMeta?: { preneur?: number; contrat?: Contract };
  /** Chien affiché, en attente clic ou timeout avant la suite */
  awaitingChienDismiss?: boolean;
  /** Écart serveur reçu pendant l'affichage du chien */
  ecartArrivedDuringChien?: boolean;

  // Cartes
  myHand: CardNumber[];
  currentTrick: CardNumber[];
  trickPlayerPositions: number[]; // Position du joueur pour chaque carte du pli
  lastTrick: CardNumber[];
  chien?: CardNumber[];
  
  // Plis
  numPli: number;
  
  // Flag pour indiquer qu'un pli est complet (déclenche la fin de pli)
  trickComplete?: boolean;
  
  // Score de la donne (affiché en phase 99999)
  scores?: ScoreData;
  
  // Contraintes d'enchères (dérivées de contratObligatoire dans distribue_cartes)
  // min/max définissent la plage des contrats autorisés (0=passe, 1=petite, ..., 4=garde contre)
  bidConstraints?: { min: number; max: number };
  
  // Temps limites de la table (transmis via infos_table)
  timeLimits?: TimeLimits;
  
  // Timestamp (ms) du début du tour du joueur courant (pour le décompte)
  // Initialisé quand c'est au joueur d'agir, basé sur le moment de réception du message serveur
  turnStartedAt?: number;

  /** Chrono global de donne Live (timeout_attaquant / timeout_defenseur), en secondes */
  dealTimeoutSeconds?: number;
  /** Timestamp (ms) de réception de distribue_cartes — départ du chrono de donne */
  dealStartedAt?: number;
  
  // Poignée déclarée actuellement affichée (null si aucune)
  declaredPoignee?: DeclaredPoignee;
}

// ============================================
// Types pour les actions
// ============================================

export type GameAction =
  // Actions de délai (pour les animations)
  | { type: 'delay'; ms: number }
  | { type: 'bid_pre_delay'; ms: number }  // Délai de réflexion avant enchère (skippé si c'est notre propre enchère)
  | { type: 'bid_advance' }                // Avance au joueur suivant après affichage de l'enchère
  
  // Actions de jeu
  | { type: 'distribue'; data: DistribueData }
  | { type: 'carte'; data: CarteData }
  | { type: 'fin_pli'; data: FinPliData }
  | { type: 'contrat'; data: ContratData }
  | { type: 'chien'; data: ChienData }
  | { type: 'chien_vu' }  // Le joueur a vu le chien, passer à l'écart
  | { type: 'ecart'; data: EcartData }  // Message du serveur (écart d'un autre joueur)
  | { type: 'ecart_done'; data: { cartes: CardNumber[] } }  // Le joueur humain a validé son écart
  | { type: 'appel'; data: AppelData }
  | { type: 'king_call_reveal_done' }
  | { type: 'score'; data: ScoreData }
  
  // Actions de poignée
  | { type: 'poignee_announce'; data: DeclaredPoignee }  // Afficher une poignée déclarée
  | { type: 'poignee_hide' }                             // Masquer la poignée affichée
  
  // Actions de joueurs
  | { type: 'joueurs'; data: JoueursData }
  | { type: 'infos_table'; data: InfosTableData }
  | { type: 'set_position'; position: number }
  
  // Neo-specific: update hand without changing phase
  | { type: 'hand_update'; hand: CardNumber[] }
  // Neo lobby: en attente des autres joueurs / démarrage
  | { type: 'neo_waiting_lobby'; data: {
      players: Array<{ pseudo: string; position: number; isBot: boolean; isConnected: boolean }>;
      adminPseudo: string;
    } };

// Données des actions
export interface DistribueData {
  donneur: number;
  cartes: number[];
  contrats: number[];
  chelems?: string[];
  etat: number;
  numPli?: number;
  pliEnCours?: number[];
  entame?: number;
  chien?: number[];
  idPartie?: string;
  ip?: string;
  port?: number;
  contratObligatoire?: number;  // -1=libre, 0-4=forcé, 7-10=minimum forcé (11-cO)
  appelObligatoire?: number;    // 1=appel obligatoire du roi
  /** Reprise mid-deal : joueur courant exact (Neo sync) */
  syncCurrentPlayer?: number;
  /** Reprise mid-deal : positions des joueurs dans le pli en cours */
  trickPlayerPositions?: number[];
  /** Chrono global de donne Live, en secondes (timeout_attaquant / timeout_defenseur) */
  tempsDonne?: number;
}

export interface CarteData {
  joueur: number | string;
  carte: number | string;
  poignee?: string;
  doublePoignee?: string;
  triplePoignee?: string;
  contenuPoignee?: string;
}

export interface FinPliData {
  gagnant: number | string;
  pli?: number[];
}

export interface ContratData {
  joueur: number | string;
  contrat: number | string;
  chelem?: string;
}

export interface ChienData {
  cartes: number[];
  preneur?: number;
  contrat?: number;
}

export interface EcartData {
  ecart?: number[];
}

export interface AppelData {
  couleur: number | string;
  appelDame?: string;
}

export interface ScoreData {
  // Données du serveur Java
  contrat?: number;
  contratRealise?: string | boolean;
  pointsJoueur?: number;
  difference?: number;
  nbBouts?: number;
  pointsAttaque?: number;
  scores?: number[];

  // Legacy field names
  points_joueur?: number;
  points_defense?: number;
  pointsNecessaires?: number;
  multiplicateur?: number;
  poignee?: number;
  petitAuBout?: number;
  chelem?: number;
  
  // Compatibilité
  points?: number;
  gagnant?: number;
  
  // Neo: all players passed
  allPassed?: boolean;
}

export interface JoueursData {
  joueurs: Array<{ pseudo: string; position: number; connecte?: boolean }>;
}

export interface InfosTableData {
  pseudos?: string[];
  commentaire?: string;
  position?: number;
  connectes?: string[];  // ['1', '', '', ''] - '1' = humain connecté, '' = bot
  tempsLimite?: (string | number)[];  // [contrat, appel, ecart, carte, score] en secondes
}

// ============================================
// Configuration des délais (en ms)
// ============================================

export function parsePositiveSeconds(value: unknown): number | undefined {
  if (value === undefined || value === null || value === '') return undefined;
  const n = Number(value);
  return Number.isFinite(n) && n > 0 ? Math.round(n) : undefined;
}

export const DELAYS = {
  CARD_PLAYED: 400,      // Délai après qu'une carte soit jouée
  TRICK_END: 1000,       // Délai avant d'effacer le pli
  BID_THINKING: 200,     // Délai de "réflexion" avant l'annonce d'une enchère (skippé pour le joueur local)
  BID_DISPLAY: 1500,     // Délai minimum d'affichage après une enchère (incompressible)
  CHIEN_SHOWN: 8000,     // Délai pour voir le chien (8 secondes)
  KING_CALL_REVEAL: 5000, // Révélation du roi appelé (5 joueurs) avant le chien
  ALL_PASSED: 5000,      // Délai quand tout le monde passe avant fin
  POIGNEE_DISPLAY: 3000, // Délai d'affichage d'une poignée déclarée (3 secondes)
} as const;

// ============================================
// Conversion des messages serveur en actions
// ============================================

/**
 * Convertit un message du serveur en une ou plusieurs actions
 * Peut retourner plusieurs actions (ex: carte + delay)
 */
export function serverMessageToActions(message: { type: string; vals: any }): GameAction[] {
  const actions: GameAction[] = [];
  
  switch (message.type) {
    case 'distribue_cartes':
    case 'distribue': {
      // Distribue est traité immédiatement (pas de délai)
      // Il initialise l'état du jeu
      actions.push({
        type: 'distribue',
        data: {
          donneur: Number(message.vals.donneur),
          cartes: message.vals.cartes || [],
          contrats: message.vals.contrats || [],
          chelems: message.vals.chelems,
          etat: Number(message.vals.etat || 0),
          numPli: Number(message.vals.numPli || 0),
          pliEnCours: message.vals.pliEnCours || [],
          entame: message.vals.entame !== undefined ? Number(message.vals.entame) : undefined,
          chien: message.vals.chien,
          idPartie: message.vals.idPartie !== undefined ? String(message.vals.idPartie) : undefined,
          ip: message.vals.ip,
          port: message.vals.port,
          contratObligatoire: message.vals.contratObligatoire !== undefined ? Number(message.vals.contratObligatoire) : undefined,
          appelObligatoire: message.vals.appelObligatoire !== undefined ? Number(message.vals.appelObligatoire) : undefined,
          tempsDonne: parsePositiveSeconds(message.vals.tempsDonne),
        },
      });
      break;
    }
    
    case 'carte': {
      const joueur = Number(message.vals.joueur);
      
      // Détecter si une poignée est déclarée avec cette carte
      let poigneeType = PoigneeType.None;
      if (message.vals.poignee === 'true') poigneeType = PoigneeType.Simple;
      if (message.vals.doublePoignee === 'true') poigneeType = PoigneeType.Double;
      if (message.vals.triplePoignee === 'true') poigneeType = PoigneeType.Triple;
      
      // Si poignée déclarée, insérer les actions d'affichage AVANT la carte
      if (poigneeType !== PoigneeType.None && message.vals.contenuPoignee) {
        // Parser le contenu de la poignée (string comma-separated ou array)
        let poigneeCards: CardNumber[] = [];
        if (typeof message.vals.contenuPoignee === 'string') {
          poigneeCards = message.vals.contenuPoignee
            .split(',')
            .map((c: string) => Number(c.trim()))
            .filter((c: number) => c > 0) as CardNumber[];
        } else if (Array.isArray(message.vals.contenuPoignee)) {
          poigneeCards = message.vals.contenuPoignee
            .map((c: any) => Number(c))
            .filter((c: number) => c > 0) as CardNumber[];
        }
        
        if (poigneeCards.length > 0) {
          actions.push({
            type: 'poignee_announce',
            data: {
              player: joueur,
              type: poigneeType,
              cards: poigneeCards,
            },
          });
          actions.push({ type: 'delay', ms: DELAYS.POIGNEE_DISPLAY });
          actions.push({ type: 'poignee_hide' });
        }
      }
      
      // Carte jouée: action + délai pour l'animation
      actions.push({
        type: 'carte',
        data: {
          joueur,
          carte: Number(message.vals.carte),
          poignee: message.vals.poignee,
          doublePoignee: message.vals.doublePoignee,
          triplePoignee: message.vals.triplePoignee,
          contenuPoignee: message.vals.contenuPoignee,
        },
      });
      actions.push({ type: 'delay', ms: DELAYS.CARD_PLAYED });
      break;
    }
    
    case 'pli':
    case 'fin_pli': {
      // Fin de pli: délai pour voir les cartes, puis effacer
      actions.push({ type: 'delay', ms: DELAYS.TRICK_END });
      actions.push({
        type: 'fin_pli',
        data: {
          gagnant: Number(message.vals.gagnant),
          pli: message.vals.pli,
        },
      });
      break;
    }
    
    case 'contrat': {
      // Enchère annoncée : 3 étapes séparées
      // 1) bid_pre_delay : temps de réflexion (skippé si c'est notre propre enchère)
      // 2) contrat : affiche l'enchère (currentPlayer reste sur l'enchérisseur)
      // 3) bid_advance : après un délai d'affichage, avance au joueur suivant
      actions.push({ type: 'bid_pre_delay', ms: DELAYS.BID_THINKING });
      actions.push({
        type: 'contrat',
        data: {
          joueur: Number(message.vals.joueur),
          contrat: Number(message.vals.contrat),
          chelem: message.vals.chelem,
        },
      });
      actions.push({ type: 'bid_advance' });
      break;
    }
    
    case 'chien': {
      // Le serveur peut envoyer le preneur et le contrat avec le chien
      actions.push({
        type: 'chien',
        data: {
          cartes: message.vals.cartes || [],
          preneur: message.vals.preneur !== undefined ? Number(message.vals.preneur) : undefined,
          contrat: message.vals.contrat !== undefined ? Number(message.vals.contrat) : undefined,
        },
      });
      // Ajouter un délai pour voir le chien avant de passer à la suite
      // Ce délai sera skippable par un clic
      actions.push({ type: 'delay', ms: DELAYS.CHIEN_SHOWN });
      // Après le délai, passer automatiquement à la phase suivante
      actions.push({ type: 'chien_vu' });
      break;
    }
    
    case 'ecart': {
      actions.push({
        type: 'ecart',
        data: {
          ecart: message.vals.ecart,
        },
      });
      break;
    }
    
    case 'appel': {
      actions.push({
        type: 'appel',
        data: {
          couleur: Number(message.vals.couleur),
          appelDame: message.vals.appelDame,
        },
      });
      break;
    }
    
    case 'score': {
      actions.push({
        type: 'score',
        data: message.vals,
      });
      break;
    }
    
    case 'joueurs': {
      actions.push({
        type: 'joueurs',
        data: {
          joueurs: message.vals.joueurs || [],
        },
      });
      break;
    }
    
    case 'infos_table': {
      actions.push({
        type: 'infos_table',
        data: {
          pseudos: message.vals.pseudos,
          commentaire: message.vals.commentaire,
          position: message.vals.position !== undefined ? Number(message.vals.position) : undefined,
          connectes: message.vals.connectes,  // ['1', '', '', ''] pour identifier humains vs bots
          tempsLimite: message.vals.tempsLimite,  // Temps limites de la table
        },
      });
      break;
    }
    
    default:
      // Message non géré - on l'ignore pour l'instant
      log.debug(`[GameActions] Unhandled message type: ${message.type}`, message.vals);
      break;
  }
  
  return actions;
}

// ============================================
// État initial
// ============================================

export function createInitialGameState(tableId: string): GameState {
  return {
    tableId,
    idPartie: undefined,
    phase: 0, // Waiting
    playerCount: 4,
    players: [],
    myPosition: 0,
    hasOtherHumans: false,  // Par défaut, on suppose qu'il n'y a pas d'autres humains
    dealer: 0,
    currentPlayer: 0,
    myHand: [],
    currentTrick: [],
    trickPlayerPositions: [],
    lastTrick: [],
    numPli: 0,
    timeLimits: undefined,
    turnStartedAt: undefined,
    dealTimeoutSeconds: undefined,
    dealStartedAt: undefined,
    declaredPoignee: undefined,
  };
}
